import { PlaygroundUserMessage } from 'src/components/PlaygroundChat/PlaygroundUserMessage/PlaygroundUserMessage';
import { PlaygroundAIMessage } from 'src/components/PlaygroundChat/PlaygroundAIMessage/PlaygroundAIMessage';
import { clsx } from 'clsx';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ReplyPlaygroundButton } from 'src/components/PlaygroundChat/ReplyPlaygroundButton/ReplyPlaygroundButton';
import { sendSSEMessage } from 'src/utils/sse.utils';
import {
  appendPlaygroundMessages,
  clearPlayground,
  setPlayground,
  updateLastPlaygroundMessage,
} from 'src/store/slices/playgroundSlices';
import { API_VERSION, PATH_PLAYGROUND_API } from 'src/constants/api-routes';
import { getEnvironment } from 'src/env/env';
import {
  useCreatePlaygroundChatMutation,
  useGetProjectByIdQuery,
  useLazyGetPlaygroundChatQuery,
} from 'src/services';
import { useAppDispatch, useAppSelector } from 'src/store';
import { useLoader } from 'src/provider/LoaderProvider';
import { useNotification } from 'src/hooks/useNotification';
import { uuid } from 'src/utils/uuid';
import moment from 'moment/moment';
import { RefreshPlaygroundButton } from 'src/components/PlaygroundChat/RefreshPlaygroundButton/RefreshPlaygroundButton';
import { useParams } from 'react-router-dom';
import { PlaygroundDateMessage } from 'src/components/PlaygroundChat/PlaygroundDateMessage/PlaygroundDateMessage';
import { PlaygroundSettingsVersion } from 'src/components/PlaygroundChat/PlaygroundSettingsVersion/PlaygroundSettingsVersion';
import { PlaygroundResolvedMessage } from 'src/components/PlaygroundChat/PlaygroundResolvedMessage/PlaygroundResolvedMessage';
import { PlaygroundEscalatedMessage } from 'src/components/PlaygroundChat/PlaygroundEscalatedMessage/PlaygroundEscalatedMessage';
import { ERROR_MESSAGE_PLAYGROUND, ISO_DATE_FORMAT } from 'src/shared/constants';
import { phEvent } from 'src/utils/posthog.utils';
import { FullScreenButton } from 'src/components/ButtonWithIcon/FullScreenButton';
import { getErrorMessage } from 'src/utils/get-error-message';

const INITIAL_MESSAGE = `Hi there 👋 \n\n This is the Playground — a safe space to test customer conversations. Pretend to be your customer and ask me anything.`;

export const PlaygroundChat = ({
  name = '',
  className = '',
  isTitle = false,
  titleClassName = '',
  isFullScreenBtn = false,
  fullScreenBtnClick = () => {},
  settings = null,
  disabled = false,
  userMessageClassName = '',
  aIMessageClassName = '',
  chatContainerClassName = '',
  replyContainerClassName = '',
  versionHidden = false,
}) => {
  const { id } = useParams();
  const [isLastMessageLoading, setIsLastMessageLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [chat, setChat] = useState(null);
  const [escalatedAt, setEscalatedAt] = useState('');
  const [resolvedAt, setResolvedAt] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const chatContainerRef = useRef(null);
  const textareaRef = useRef(null);
  const loader = useLoader();
  const notification = useNotification();
  const { playground, PLAYGROUND_PREFIX } = useAppSelector((s) => s.playgroundSlices);
  const dispatch = useAppDispatch();
  const { managedAccountId } = useAppSelector((s) => s.companySlices);
  const prefix = `${name}_` || PLAYGROUND_PREFIX;

  const { data: projectData } = useGetProjectByIdQuery({
    id: id,
  }) as any;

  const [
    createPlaygroundChat,
    {
      data: createPlaygroundChatData,
      error: createPlaygroundChatError,
      isLoading: isLoadingCreatePlaygroundChat,
    },
  ] = useCreatePlaygroundChatMutation();
  const [
    fetchPlaygroundChat,
    { data: getPlaygroundChatData, isLoading: isLoadingGetPlaygroundChat },
  ] = useLazyGetPlaygroundChatQuery() as any;

  const sendMessage = useCallback(
    async (message) => {
      const environment = await getEnvironment();
      const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;
      const urlStr = `${environment.APP_BASE_URL}/${version}/${PATH_PLAYGROUND_API.ROOT}/${id}${PATH_PLAYGROUND_API.CHAT}/${chat._id}${PATH_PLAYGROUND_API.MESSAGES}`;
      const url = new URL(urlStr);

      if (settings && settings.id) {
        url.searchParams.set('settings_id', settings.id);
      }

      if (managedAccountId) {
        url.searchParams.set('account', managedAccountId);
      }

      const chatData = playground && playground[`${prefix}${id}`];

      if (chatData?.messages?.length < 2) {
        phEvent('send_playground_message');
      }

      sendSSEMessage('POST', url.toString(), message, (response) => {
        const { error, message } = response;

        if (error) {
          notification.error(error);
          dispatch(updateLastPlaygroundMessage({ prefix, projectId: id, message: null }));
        } else {
          dispatch(updateLastPlaygroundMessage({ prefix, projectId: id, message }));
        }
      });
    },
    [id, chat, settings, managedAccountId],
  );

  useEffect(() => {
    const chatData = playground && playground[`${prefix}${id}`];

    if (chatData) {
      fetchPlaygroundChat({ projectId: id, chatId: chatData._id });
    } else {
      createPlaygroundChat({ projectId: id });
    }
  }, [id]);

  useEffect(() => {
    const chatData = playground[`${prefix}${id}`];
    const chat = [];
    const date = {
      today: moment(),
      yesterday: moment().add(-1, 'days'),
      current: null,
    };

    if (!chatData) {
      return;
    }

    setEscalatedAt('');
    setResolvedAt('');

    if (chatData && !chatData?.messages?.length) {
      const message = createMessage(INITIAL_MESSAGE, 'ai', chatData);

      dispatch(appendPlaygroundMessages({ prefix, projectId: id, messages: [message] }));
    }

    chatData.messages?.forEach((message, index, array) => {
      const isLastMessage = index === array.length - 1;
      const createdAt = moment(message.created_at);
      if (!date.current || !createdAt.isSame(moment(date.current), 'day')) {
        date.current = moment(message.created_at);
        let value;

        if (date.today.isSame(date.current, 'day')) {
          value = 'Today';
        } else if (date.yesterday.isSame(date.current, 'day')) {
          value = 'Yesterday';
        } else {
          value = date.current.format('ll');
        }

        const dateMessage = {
          type: 'date',
          value,
        };

        chat.push(dateMessage);
      }

      if (isLastMessage && message.end_chat) {
        setResolvedAt(message.created_at as string);
      }

      if (isLastMessage && message.use_manager) {
        setEscalatedAt(message.created_at as string);
        return;
      }

      chat.push(message);
    });

    switch (chatData && chatData?.status) {
      case 'closed':
      case 'auto_closed':
        setEscalatedAt('');
        setResolvedAt(chat[chat.length - 1].created_at);
        break;
      case 'escalated':
        setResolvedAt('');
        setEscalatedAt(chat[chat.length - 1].created_at);
        break;
    }

    setChat({ ...chatData, messages: chat });
  }, [playground]);

  useEffect(() => {
    const isLoading = chat?.messages && chat?.messages[chat.messages.length - 1].loading;
    scrollToBottom();
    setIsLastMessageLoading(Boolean(isLoading));
  }, [chat]);

  useEffect(() => {
    if (createPlaygroundChatData) {
      dispatch(setPlayground({ prefix, projectId: id, playground: createPlaygroundChatData }));
    }
  }, [createPlaygroundChatData]);

  useEffect(() => {
    if (getPlaygroundChatData) {
      const message = createMessage(INITIAL_MESSAGE, 'ai', getPlaygroundChatData);
      const messages = getPlaygroundChatData.messages || [];
      const payload = {
        prefix,
        projectId: id,
        playground: { ...getPlaygroundChatData, messages: [message, ...messages] },
      };

      dispatch(setPlayground(payload));
    }
  }, [getPlaygroundChatData]);

  useEffect(() => {
    if (isLoadingGetPlaygroundChat) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [isLoadingGetPlaygroundChat]);

  useEffect(() => {
    if (isLoadingCreatePlaygroundChat) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [isLoadingCreatePlaygroundChat]);

  useEffect(() => {
    if (!isLastMessageLoading) {
      textareaRef?.current?.focus();
    }
  }, [isLastMessageLoading]);

  useEffect(() => {
    if (createPlaygroundChatError) {
      const message = getErrorMessage(createPlaygroundChatError);
      const friendlyMessage =
        message === ERROR_MESSAGE_PLAYGROUND.BACKEND_ACTIVE_PLAN
          ? ERROR_MESSAGE_PLAYGROUND.FRIENDLY_ACTIVE_PLAN
          : ERROR_MESSAGE_PLAYGROUND.FRIENDLY_ERROR_MESSAGE;

      setErrorMessage(friendlyMessage);
    } else {
      setErrorMessage('');
    }
  }, [createPlaygroundChatError]);

  const createMessage = (text, owner, chatData, loading = false) => {
    const isInitialMessage = text === INITIAL_MESSAGE;
    const date = isInitialMessage
      ? (chatData.messages && chatData.messages[0]?.created_at) || undefined
      : new Date();

    return {
      id: uuid(),
      project_id: id,
      body: text,
      created_at: moment.utc(date).format(ISO_DATE_FORMAT),
      loading,
      owner,
      chat_id: chatData._id,
    };
  };

  const onKeyDown = (event) => {
    if (event.key === 'Enter' && event.shiftKey) {
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      Boolean(inputValue) && applyMessages();
    }
  };

  const onButtonClick = () => {
    Boolean(inputValue) && applyMessages();
  };

  const applyMessages = async () => {
    const message = {
      user: createMessage(inputValue, 'user', chat._id),
      ai: createMessage('', 'ai', chat._id, true),
    };

    setInputValue('');
    dispatch(
      appendPlaygroundMessages({ prefix, projectId: id, messages: [message.user, message.ai] }),
    );
    await sendMessage(inputValue);
  };

  const scrollToBottom = () => {
    const scrollHeight = chatContainerRef.current.scrollHeight;
    const height = chatContainerRef.current.clientHeight;
    const maxScrollTop = scrollHeight - height;
    chatContainerRef.current.scrollTop = maxScrollTop > 0 ? maxScrollTop : 0;
  };

  const onRefresh = () => {
    dispatch(clearPlayground({ prefix, projectId: id }));
    createPlaygroundChat({ projectId: id });
    notification.success(`Playground chat refreshed.`);
  };

  return (
    <div
      className={clsx(
        'flex flex-auto flex-col items-center w-full overflow-hidden gap-2.5',
        className,
      )}
    >
      <div className="flex w-full justify-between card-header !min-h-fit">
        <div className={clsx('title cursor-default', titleClassName)}>
          {isTitle && <span className="!no-underline max-md:hidden">Playground</span>}
          {settings && !versionHidden && <PlaygroundSettingsVersion settings={settings} />}
        </div>
        <div className="flex items-center gap-2.5">
          <RefreshPlaygroundButton
            onClick={onRefresh}
            className={'self-end'}
            disabled={!projectData?.is_active || disabled}
          />
          {isFullScreenBtn && <FullScreenButton onClick={fullScreenBtnClick} />}
        </div>
      </div>
      <div
        ref={chatContainerRef}
        className={clsx(
          disabled && 'opacity-50',
          'flex flex-auto w-full  overflow-auto justify-center relative scroll-smooth max-w-[600px] h-1',
          chatContainerClassName,
        )}
      >
        {errorMessage ? (
          <div
            className={
              'flex text-center self-center text-sm font-medium text-ebony_opacity_30 shadow justify-self-center bg-white border border-light rounded-sm-5 p-5 whitespace-pre-line'
            }
          >
            {errorMessage}
          </div>
        ) : (
          <ul className="flex flex-col gap-[5px] p-1 w-full flex-1 lg:w-2/3">
            {chat?.messages?.map((message, index) => {
              if (message.type === 'date') {
                return (
                  <PlaygroundDateMessage date={message.value} key={`${message.value}-${index}`} />
                );
              }

              return message.owner === 'user' ? (
                <PlaygroundUserMessage
                  key={`${message.created_at}-${index}`}
                  message={message}
                  className={userMessageClassName}
                />
              ) : (
                <PlaygroundAIMessage
                  key={`${message.created_at}-${index}`}
                  message={message}
                  className={aIMessageClassName}
                />
              );
            })}
            {Boolean(escalatedAt) && <PlaygroundEscalatedMessage date={escalatedAt} />}
            {Boolean(resolvedAt) && <PlaygroundResolvedMessage date={resolvedAt} />}
          </ul>
        )}
      </div>
      <div
        className={clsx(
          (!projectData?.is_active || disabled) && 'opacity-50',
          'flex-initial w-full relative max-w-[600px]',
          replyContainerClassName,
        )}
      >
        <textarea
          ref={textareaRef}
          placeholder="Write a reply..."
          disabled={
            !projectData?.is_active ||
            isLastMessageLoading ||
            Boolean(escalatedAt) ||
            Boolean(resolvedAt) ||
            Boolean(errorMessage) ||
            disabled
          }
          rows={3}
          value={inputValue}
          onChange={(e) => setInputValue(e.currentTarget.value)}
          className={
            'w-full border border-[#E3E6E8] !outline-none outline-ebony_opacity_10 resize-none rounded-sm-5 py-2.5 pl-5 pr-11 placeholder:text-[#E3E6E8] text-sm'
          }
          onKeyDown={onKeyDown}
        />
        <ReplyPlaygroundButton
          onClick={onButtonClick}
          disabled={
            !projectData?.is_active ||
            !inputValue ||
            isLastMessageLoading ||
            Boolean(escalatedAt) ||
            Boolean(resolvedAt) ||
            Boolean(errorMessage) ||
            disabled
          }
        />
      </div>
    </div>
  );
};
